import { message, Modal, Table, Select } from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment";
import "../EventsStyle/EventLogoutModal.css";
import { clientLogout, fetchClientSession } from "../EventApis";

const EventLogOutModal = ({ isOpenLogin, onCloseLOgin, clientId, fetchClientData }) => {
  const [timeLeft, setTimeLeft] = useState(3600);
  const [timeIn, setTimeIn] = useState(0);
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  useEffect(() => {
    if (client?.activeSessions?.length > 0) {
      const stillActive = client.activeSessions.some(
        (s) => s._id === selectedSessionId
      );
      if (!stillActive) {
        setSelectedSessionId(client.activeSessions[0]._id);
      }
    } else {
      setSelectedSessionId(null);
    }
  }, [client, selectedSessionId]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpenLogin) {
      setTimeLeft(3600);
      setTimeIn(0);
      setClient(null);
      setSelectedSessionId(null);
    } else {
      fetchClientLatestSession();
    }
  }, [isOpenLogin]);
  
  useEffect(() => {
  }, [timeLeft, timeIn]);


 

  const currentSession =
    client?.activeSessions?.find((s) => s._id === selectedSessionId) ||
    client?.activeSessions?.[0] ||
    client;

  useEffect(() => {
    const startTime = currentSession?.loginTime;
    if (isOpenLogin && startTime) {
      const { remainingTime, elapsedTime } = calculateTimeLeft(startTime);
      setTimeLeft(remainingTime);
      setTimeIn(elapsedTime);

      const timeLeftInterval = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timeLeftInterval);
            handleLogout();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      const timeInInterval = setInterval(() => {
        setTimeIn((prevTime) => prevTime + 1);
      }, 1000);

      return () => {
        clearInterval(timeLeftInterval);
        clearInterval(timeInInterval);
      };
    }
  }, [isOpenLogin, selectedSessionId, currentSession?.loginTime]);

  const calculateTimeLeft = (loginTime) => {
    if (loginTime) {
      const loginMoment = moment(loginTime);
      const elapsedTime = moment().diff(loginMoment, "seconds"); // Actual elapsed time
      const remainingTime = Math.max(3600 - elapsedTime, 0); // Max time is 3600
  
      return { remainingTime, elapsedTime };
    }
    return { remainingTime: 3600, elapsedTime: 0 };
  };

  const fetchClientLatestSession = async () => {
    setIsLoading(true);
    try {
      const targetUserId =
        (typeof clientId === "string" ? clientId : clientId?._id) ||
        client?.userId?._id ||
        (typeof client?.userId === "string" ? client?.userId : null);

      if (!targetUserId) {
        setIsLoading(false);
        return;
      }

      const payload = { userId: targetUserId };
      const response = await fetchClientSession(payload);
      if (response && (response.success || response.status === 200) && response.data) {
        setClient(response.data);
      } else {
        // No active session found anymore
        if (typeof onCloseLOgin === "function") {
          onCloseLOgin();
        }
      }
    } catch (error) {
      console.warn("fetchClientLatestSession warning:", error);
      if (typeof onCloseLOgin === "function") {
        onCloseLOgin();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const targetUserId =
        client?.userId?._id ||
        (typeof client?.userId === "string" ? client?.userId : null) ||
        (typeof clientId === "string" ? clientId : clientId?._id);

      if (!targetUserId) {
        throw new Error("Client ID is not available. Cannot log out.");
      }

      setIsLoggingOut(true);
      const data = await clientLogout(targetUserId, selectedSessionId);

      if (data && (data?.status === 200 || data?.success)) {
        message.success(data.message || "Client successfully logged out");

        if (typeof fetchClientData === "function") {
          try {
            await fetchClientData();
          } catch (fetchErr) {
            console.warn("fetchClientData warning:", fetchErr);
          }
        }

        const remainingSessions = (client?.activeSessions || []).filter(
          (s) => s._id !== selectedSessionId
        );

        if (remainingSessions.length > 0) {
          await fetchClientLatestSession();
        } else {
          if (typeof onCloseLOgin === "function") {
            onCloseLOgin();
          }
        }
      } else {
        message.error(data?.message || "Logout failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during logout:", error);
      message.error(error.message || "Logout failed. Please try again.");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const formatTime = (timeLeft) => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
  };

  const loginTime = client?.loginData?.loginTime
    ? moment(client?.loginData?.loginTime)
    : moment();

  const dataSource = [
    { key: "1", label: "Login Date", value: currentSession?.loginTime ? moment(currentSession.loginTime).format("DD-MM-YYYY") : "N/A" },
    { key: "2", label: "Login Time", value: currentSession?.loginTime ? moment(currentSession.loginTime).format("HH:mm:ss") : "N/A" },
    { key: "3", label: "Client Name", value: `${currentSession?.name || client?.name || ""}`.trim() || "N/A" },
    { key: "4", label: "UCC Number", value: client?.userId?.uccNumber || "N/A" },
    { key: "5", label: "Box Number", value: client?.userId?.boxNumber || "N/A" },
  ];

  const columns = [
    { title: "Field", dataIndex: "label", key: "label", render: (text) => <strong>{text}</strong> },
    { title: "Value", dataIndex: "value", key: "value" },
  ];

  return (
    <Modal
      open={isOpenLogin}
      onCancel={() => {
        if (typeof onCloseLOgin === "function") {
          onCloseLOgin();
        }
      }}
      footer={null}
      centered
      width={500}
    >
      <h3 className="header-text fs-4 text-center">Active Client Details</h3>
      <div className="modal-content border-0">
        <div className="client-details-section">
          <div className="row">
            <div style={{ backgroundColor: "red", color: "white" }} className="col card time-left-card mx-2">
              <strong>Time left:</strong> {formatTime(timeLeft)}
            </div>
            <div style={{ backgroundColor: "blue", color: "white" }} className="col card time-left-card mx-2">
              <strong>Time In:</strong> {formatTime(timeIn)}
            </div>
          </div>

          <div>
            <Table dataSource={dataSource} columns={columns} pagination={false} />
          </div>
        </div>

        {client?.activeSessions?.length > 1 && (
          <div className="d-flex justify-content-center align-items-center mb-3 mt-3">
            <strong className="me-2 text-dark">Select Person:</strong>
            <Select
              value={selectedSessionId}
              onChange={setSelectedSessionId}
              style={{ minWidth: 220 }}
              options={client.activeSessions.map((s) => ({
                label: `${s.name} (${formatTime(calculateTimeLeft(s.loginTime).remainingTime)} left)`,
                value: s._id,
              }))}
            />
          </div>
        )}

        <div className="d-flex justify-content-center mt-2">
          <button
            className="btn-login"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging Out..." : "Log Out"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EventLogOutModal;
